import type { WorkPhase } from "@myapp/contract";
import { PhaseActionBar } from "../../../features/work-session";
import type { PrimaryWorkAction } from "../../../features/work-session";
import { AppointmentSecondaryActions } from "./AppointmentSecondaryActions";

type Props = {
  /** From getPrimaryWorkAction — null renders nothing (the page hides the bar entirely). */
  action: PrimaryWorkAction | null;
  /** From deriveWorkPhase — drives the secondary buttons. */
  phase: WorkPhase | null;
  /** ISO anchor of the running phase — feeds the elapsed chip. */
  elapsedSince: string | null;
  isPending: boolean;
  failed: boolean;
  confirmedAt: string | null;
  onAction: (action: PrimaryWorkAction) => void;
  onUndo?: () => void;
  onFollowUp: () => void;
  onReschedule: () => void;
};

/**
 * The mobile bottom bar. A floating, capped-width card, fully rounded, with real air below it —
 * not an edge-to-edge strip and not flush against the screen edge either.
 *
 * `position: fixed`, not `absolute`. `absolute` anchors to the nearest *positioned* ancestor,
 * which here would have been whatever the DOM happens to nest this under — fragile, and exactly
 * the kind of thing that can quietly put the bar somewhere other than the true viewport bottom.
 * `fixed` always anchors to the viewport itself, so `bottom-0` means the actual screen edge on
 * every device regardless of what wraps it.
 *
 * Being taken out of `PageLayout`'s flex column this way is what commit 7d75d2d reverted once,
 * because `.content` no longer shrank to leave the bar room and the page's last card sat hidden
 * behind it. `WorkerAppointmentPage` now reserves matching space explicitly (`pb-32` on its
 * mobile-only content wrapper) instead, so that regression does not return.
 *
 * The gap below the card is `margin-bottom: max(0.75rem, env(safe-area-inset-bottom))` — at least
 * 12px of visible air, and never less than the device's own safe-area inset (the same value that
 * describes the iOS home indicator and a device's own rounded screen corners), so the card floats
 * clear of both instead of being clipped by whichever one is taller on a given phone.
 *
 * The primary phase button and the (at most one) secondary action share a single row instead of
 * stacking — `flex-1` on both, so whichever secondary button renders splits the width evenly with
 * the primary one; a lone primary button (ANFAHRT/ABFAHRT, which never pair with a secondary
 * button) simply fills the row. Both sides pass `compact`/shortened labels sized for that half
 * width — see `PrimaryWorkButton` and `AppointmentSecondaryActions`.
 */
export const AppointmentActions = ({
  action,
  phase,
  elapsedSince,
  isPending,
  failed,
  confirmedAt,
  onAction,
  onUndo,
  onFollowUp,
  onReschedule,
}: Props) => (
  // `fixed` escapes `.page`'s own horizontal padding entirely, so it is restated here (`px-4`,
  // matching `.page`'s padding at this breakpoint) rather than relied on.
  <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-md px-4">
    <div
      className="rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_4px_20px_rgba(15,23,42,0.12)]"
      style={{ marginBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-stretch gap-2">
        {action && (
          <PhaseActionBar
            action={action}
            className="min-w-0 flex-1"
            elapsedSince={elapsedSince}
            isPending={isPending}
            failed={failed}
            confirmedAt={confirmedAt}
            onAction={onAction}
            onUndo={onUndo}
            compact
          />
        )}

        <AppointmentSecondaryActions
          appearance="bar"
          phase={phase}
          onFollowUp={onFollowUp}
          onReschedule={onReschedule}
        />
      </div>
    </div>
  </div>
);
